import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { 
  ClockIcon, 
  BriefcaseIcon,
  SparklesIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const Header = () => {
  const sessionData = useSelector((state) => state.interview.sessionData);
  const [isScrolled, setIsScrolled] = useState(false);

  // Sticky header on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!sessionData) return null;

  // Extract data from new API structure
  const jobTitle = sessionData.job_title || 'Interview';
  const companyName = sessionData.company_name;
  const isStarted = sessionData.is_started || false;
  const isCompleted = sessionData.is_completed || false;
  
  // Determine status
  const status = isCompleted ? 'completed' : isStarted ? 'in_progress' : 'scheduled';

  // Status configuration
  const statusConfig = {
    in_progress: {
      label: 'In Progress',
      color: 'bg-blue-100 text-blue-700 border-blue-200',
      dot: 'bg-blue-500'
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-700 border-green-200',
      dot: 'bg-green-500'
    },
    scheduled: {
      label: 'Scheduled',
      color: 'bg-gray-100 text-gray-700 border-gray-200',
      dot: 'bg-gray-500'
    }
  };

  const currentStatus = statusConfig[status] || statusConfig.in_progress;

  return (
    <header className={`sticky top-0 z-40 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-lg shadow-lg' 
        : 'bg-white shadow-sm'
    } border-b border-gray-200`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-20">
          
          {/* Left: Branding + Job Title */}
          <div className="flex items-center space-x-4 min-w-0 flex-1">
            {/* Logo/Brand */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0 group">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow">
                <SparklesIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <div className="text-sm font-bold text-gray-900 tracking-tight">
                  TeamSyncAI
                </div>
                <div className="text-xs text-gray-500">Interview Platform</div>
              </div>
            </Link>

            {/* Divider */}
            <div className="hidden md:block w-px h-8 bg-gray-300"></div>

            {/* Job Title */}
            <div className="min-w-0 flex-1">
              <div className="flex items-center space-x-2">
                <BriefcaseIcon className="w-4 h-4 text-gray-400 flex-shrink-0 hidden sm:block" />
                <h1 className="text-sm sm:text-base font-semibold text-gray-900 truncate">
                  {jobTitle}
                </h1>
              </div>
              {companyName && (
                <p className="text-xs text-gray-500 truncate hidden sm:block">
                  {companyName}
                </p>
              )}
            </div>
          </div>

          {/* Right: Help + Status */}
          <div className="flex items-center space-x-3 sm:space-x-4 flex-shrink-0">
            {/* Help Link */}
            <Link
              to="/help"
              className="p-2 text-gray-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-gray-100"
              title="Help & Support"
            >
              <QuestionMarkCircleIcon className="w-5 h-5" />
            </Link>
            
            {/* Status Badge */}
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border ${currentStatus.color} transition-colors`}>
              <div className={`w-2 h-2 rounded-full ${currentStatus.dot} animate-pulse`}></div>
              <span className="text-xs sm:text-sm font-medium">
                {currentStatus.label}
              </span>
            </div>
          </div>
        </div>

        {/* Mobile: Company Name (if exists) */}
        {companyName && (
          <div className="sm:hidden pb-3 -mt-1">
            <p className="text-xs text-gray-500 truncate">
              {companyName}
            </p>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;